import { BaseSection } from "components/layout/Section/BaseSection";
import { ReferentialKey } from "api/referential";
import { useAppSelector } from "app/hooks";
import { NsKey } from "enums/NsKey";
import { InputFactory } from "factories/InputFactory";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { selectReferentialData } from "features/referentials/selectors/selectReferentialDataByKey";
import { useFormikContext } from "formik";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useCallback, useMemo } from "react";
import { MandantsTunnelsSelector } from "back-office/shared/components/mandants/MandantsTunnelsSelector";
import { MandantSelectorPlateforme } from "back-office/shared/components/mandants/mandantTypes";
import { miaInputsConfig } from "./miaInputsConfig";
import { MiaFormFieldNames } from "./MiaFormFieldNames";
import { MiaFormValues } from "./MiaFormValues";

import { GerantSection } from 'back-office/shared/components/gerant/GerantSection';
export const MiaLayout: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const { values } = useFormikContext<MiaFormValues>();
  const referentialsData = useAppSelector(selectReferentialData);

  // Les plateformes sont choisies en premier : on résout leurs id + libellés
  // pour borner et grouper la recherche des mandants.
  const plateformeCodes = values[MiaFormFieldNames.PlateformeIds];
  const plateformes = useMemo<MandantSelectorPlateforme[]>(() => {
    const orgRefs = referentialsData[ReferentialKey.Plateforme] ?? [];
    return (plateformeCodes ?? []).flatMap((code) => {
      const ref = orgRefs.find((r) => r.code === code);
      return ref ? [{ id: ref.id, label: ref.label }] : [];
    });
  }, [plateformeCodes, referentialsData]);

  const renderField = useCallback((fieldName: keyof MiaFormValues) => {
    return (
      <InputFactory config={miaInputsConfig} fieldName={fieldName} />
    );
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <BaseSection title={t('ACTOR_FORM.MIA.SECTIONS.ORGANIZATION')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            {renderField(MiaFormFieldNames.PlateformeIds)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.MIA.SECTIONS.MANDANTS')}>
        <MandantsTunnelsSelector
          name={MiaFormFieldNames.Mandants}
          plateformes={plateformes}
        />
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.GENERAL_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            {renderField(MiaFormFieldNames.CompanyName)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Phone)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.ComplaintEmail)}
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">{t('ACTOR_FORM.PROFIL_NOTE_MANDATAIRE')}</p>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.LEGAL_INFO')}>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.RegistrationNumber)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Capital)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.RcsCity)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.RcsNumber)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.OriasNumber)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Rcp)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('COMMON.LABELS.COORDINATE')}>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6">
            {renderField(AddressFormFieldNames.Address)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.AdditionalAddress)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.PostalCode)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.City)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Country)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.USER_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.FirstName)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.LastName)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Email)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Username)}
          </div>
          <div className="col-span-1">
            {renderField(MiaFormFieldNames.Password)}
          </div>
        </div>
      </BaseSection>

      <GerantSection config={miaInputsConfig} />
    </div>
  );
};
